/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IAny, ICoreContext, IGeneric, IImageSettings, Image } from "@msdyn365-commerce/core";
import * as React from "react";
import { getCookie } from "../../../../utils/cookieutils";
import { IAccessErrorState } from "../../../../actions/access-error-state.action";

export interface IErrorComponentProps {
  context: ICoreContext<IGeneric<IAny>>;
  message: string;
  title?: string;
  redirectUrl?: string;
  countdownSeconds?: number;
  reason?: IAccessErrorState["reason"];

  /** Labels a button that reloads the page, where the check could not be completed and may succeed on retry. */
  retryLabel?: string;
}

const REDIRECT_URL_COOKIE_NAME = "redirect-url";

// Matches the 205x120 box in .hs-error__image, at twice the size for high density screens.
const ERROR_IMAGE_SETTINGS: IImageSettings = {
  viewports: {
    xs: { q: "w=410&h=240&m=6", w: 0, h: 0 },
    lg: { q: "w=410&h=240&m=6", w: 0, h: 0 },
  },
  lazyload: false,
};

const isValidUrl = (value: string | null | undefined): value is string => {
  if (!value || !value.trim()) {
    return false;
  }

  try {
    const decoded = decodeURIComponent(value.trim());
    const url = new URL(decoded);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

export const resolveRedirectUrl = (redirectUrlProp?: string): string | null => {
  const cookieUrl = getCookie(REDIRECT_URL_COOKIE_NAME);
  if (isValidUrl(cookieUrl)) {
    return decodeURIComponent(cookieUrl.trim());
  }

  if (isValidUrl(redirectUrlProp)) {
    return redirectUrlProp.trim();
  }

  return null;
};

const ErrorComponent: React.FC<IErrorComponentProps> = ({
  context,
  message,
  title,
  redirectUrl,
  countdownSeconds = 5,
  reason,
  retryLabel,
}) => {
  const [countdown, setCountdown] = React.useState(countdownSeconds);
  const errorImageUrl = (context.app.config.errorImageUrl as string | undefined)?.trim();
  const canRetry = reason === "service-unavailable" && !!retryLabel;
  const resolvedRedirectUrl = React.useMemo(
    () => (canRetry ? null : resolveRedirectUrl(redirectUrl)),
    [redirectUrl, canRetry],
  );

  React.useEffect(() => {
    setCountdown(countdownSeconds);
  }, [countdownSeconds]);

  React.useEffect(() => {
    if (!resolvedRedirectUrl) {
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          window.location.href = resolvedRedirectUrl;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [resolvedRedirectUrl, countdownSeconds]);

  const handleRedirectNow = () => {
    if (!resolvedRedirectUrl) {
      return;
    }
    window.location.href = resolvedRedirectUrl;
  };

  return (
    <div className="hs-error">
      <div className="hs-error__container">
        {errorImageUrl ? (
          <Image
            requestContext={context.actionContext.requestContext}
            src={errorImageUrl}
            altText="Access error"
            className="hs-error__image"
            gridSettings={context.request.gridSettings ?? {}}
            imageSettings={ERROR_IMAGE_SETTINGS}
            loadFailureBehavior="hide"
          />
        ) : (
          <div className="hs-error__image hs-error__image--default" aria-hidden="true" />
        )}
        <h2 className="hs-error__title">{title || "Access not granted"}</h2>
        <p className="hs-error__message">{message}</p>
        {resolvedRedirectUrl && (
          <p className="hs-error__countdown">Redirecting in {countdown} ...</p>
        )}
        {resolvedRedirectUrl && (
          <button className="hs-error__button" onClick={handleRedirectNow}>
            REDIRECT NOW
          </button>
        )}
        {canRetry && (
          <button className="hs-error__button" onClick={() => window.location.reload()}>
            {retryLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default ErrorComponent;
